/* eslint complexity: 0, max-statements: 0 */
import i18next from 'i18next';
import { isEmpty } from 'lodash';

const ONE_WEEK = 7;

export const DateUtils = {
    ONE_YEAR_IN_DAYS: 364,
    MAX_DAYS_PRIME_HOTEL: 28,

    add: (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days),
    addMonths: (date, months) =>
        new Date(date.getFullYear(), date.getMonth() + months, date.getDate()),

    compare: (a, b) => {
        if (!a || !b || !a.getTime || !b.getTime) {
            return NaN;
        }

        const timeA = a.getTime();
        const timeB = b.getTime();

        return timeA === timeB ? 0 : timeA > timeB ? -1 : 1;
    },

    toDate: (year, month, day) => new Date(year, month, day),

    getDifferenceInDays: (date1, date2) => {
        const timeDiff = Math.abs(date2.getTime() - date1.getTime());
        return Math.round(timeDiff / (1000 * 3600 * 24));
    },

    getDifferenceInMonths: (date1, date2) => {
        const diffYears = (date2.getFullYear() - date1.getFullYear()) * 12;
        const diffMonths = date2.getMonth() - date1.getMonth();
        return diffYears + diffMonths;
    },

    getToday: () => {
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    },
    isToday: (date) => DateUtils.compare(date, DateUtils.getToday()) === 0,
    isWeekend: (date) => date && (date.getDay() === 0 || date.getDay() === 6),
    getFirstDayOfMonth: (date) => new Date(date.getFullYear(), date.getMonth(), 1),
    getLastDayOfMonth: (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0),

    getFirstDayOfWeek: (week, startOn) => {
        const currentWeekDay = week.getDay();
        let diff = currentWeekDay - startOn;

        if (diff < 0) {
            diff += ONE_WEEK;
        }
        return DateUtils.add(week, diff * -1);
    },

    getTime: (date) => {
        try { return date.getTime(); } catch (err) { return 0; }
    },

    getLastValidDay: () => {
        return DateUtils.add(DateUtils.getToday(), DateUtils.ONE_YEAR_IN_DAYS);
    },

    isFuture(date) {
        return Date.parse(date) > Date.now();
    },

    isNotExpired(date) {
        return DateUtils.isFuture(date) || DateUtils.isToday(date);
    },

    isOutOfBounds(date) {
        return Date.parse(date) > DateUtils.getLastValidDay();
    },

    isValidDate(date) {
        return date instanceof Date && !Number.isNaN(date.getTime());
    },

    parse(dateStr, format = DateUtils.Format.ISO) {
        return format.parse(dateStr);
    },

    toString(date, format = DateUtils.Format.ISO) {
        return format.toString(date);
    },

    stringToDate( stringedDate ) {
        return typeof stringedDate === 'string' ? new Date(Date.parse(stringedDate)) : stringedDate;
    },

    calculateMinDateFromSegments(segments, id, minDate) {
        const index = segments.findIndex((s) => s.id === id);
        const dates = segments.map((s) => this.stringToDate(s.date)).slice(0, index);
        for (let i = dates.length - 1; i >= 0; i--) {
            if (dates[i]) {
                if (minDate > 0) {
                    return DateUtils.add(dates[i], minDate);
                }
                return dates[i];
            }
        }
        return DateUtils.getToday();
    },

    parseArrayOfDates(datesStrList) {
        return datesStrList ? datesStrList.split(',').reduce((acc, dateStr) => {
            const date = DateUtils.parse(dateStr, DateUtils.Format['DD/MM/YY']);
            if (DateUtils.isValidDate(date) && DateUtils.isNotExpired(date)) {
                acc.push(date);
            }
            return acc;
        }, []) : [];
    },

    calculateMaxDateFromSegments(segments, id, maxDays = -1) {
        const lastValidDay = DateUtils.getLastValidDay();
        if (maxDays > 0) {
            const index = segments.findIndex((s) => s.id === id);
            const dates = segments.map((s) => DateUtils.stringToDate(s.date)).slice(0, index);
            for (let i = dates.length - 1; i >= 0; i--) {
                if (dates[i]) {
                    const newDate = DateUtils.add(dates[i], maxDays);
                    return DateUtils.compare(newDate, lastValidDay) === 1 ? newDate : lastValidDay;
                }
            }
        }
        return lastValidDay;
    },

    Format: {
        // yyyy-mm-dd
        'ISO': {
            parse(dateStr) {
                if (!dateStr || isEmpty(dateStr)) {
                    return null;
                }

                const parts = dateStr.split('-').map(Number);
                // Months on new Date(string) is one-indexed
                // months on new Date(year, month, day) is zero-indexed
                parts[1] -= 1;

                return DateUtils.toDate(...parts);
            },
            toString(date) {
                return DateUtils.formatDate(date, 'yyyy-mm-dd');
            },
        },
        'DD/MM/YY': {
            parse(dateStr) {
                const dateParts = dateStr.split('/');
                return new Date(parseInt(dateParts[2]) + 2000, dateParts[1] - 1, dateParts[0]);
            },
            toString(date) {
                throw new Error('not implemented yet');
            },
        },
        // TODO: fix -> needs day names from cms :(
        // :ddd, dd mmm 'yy
        'UTC': {
            parse(dateStr) {
                throw new Error('not implemented yet');
            },
            toString(date) {
                const dateStrSplit = date.toUTCString().split(' ');
                const year = date
                    .getUTCFullYear()
                    .toString()
                    .slice(2, 4);
                const [day, dayDate, month] = dateStrSplit;
                return `${day} ${dayDate} ${month} '${year}`;
            },
        },
    },
};

DateUtils.createDateFromString = function createDateFromString(dateString) {
    let returnDate = new Date(dateString);

    try {
        if (returnDate === 'Invalid Date' && dateString) {
            // '2014-06-18 17:35:28 +0200' or '2014-06-18T17:35:28+0200' or '2014-06-18 17:35:28+02:00'
            const pattern = /([0-9-]+)[ T]([0-9:]+)\s*(\+[0-9][0-9]):?([0-9][0-9])/i;
            // is transformed to '2014-06-18T17:35:28+02:00'
            returnDate = new Date(dateString.replace(pattern, '$1T$2$3:$4'));
        } else if (!dateString) {
            throw new SyntaxError('Invalid Date');
        }
    } catch (error) {
        console.error('Invalid data.', error); // eslint-disable-line no-console
    }

    return returnDate;
};

/*
             * Date Format 1.2.3
             * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
             * MIT license

             * Includes enhancements by Scott Trenda <scott.trenda.net>
             * and Kris Kowal <cixar.com/~kris.kowal/>
             *
             * Accepts a date, a mask, or a date and a mask.
             * Returns a formatted version of the given date.
             * The date defaults to the current date/time.
             * The mask defaults to dateFormat.masks.default.
             */
/**
 * @link http://blog.stevenlevithan.com/archives/date-time-format
 */
DateUtils.formatDate = (function formatDateFactory() {
    const token = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;
    const timezone = new RegExp(
        '\\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) ' +
            '(?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\\d{4})?)\\b',
        'g',
    );
    const timezoneClip = /[^-+\dA-Z]/g;
    const pad = function pad(val, len) {
        val = String(val);
        len = len || 2;
        while (val.length < len) {
            val = '0' + val;
        }
        return val;
    };

    // Regexes and supporting functions are cached through closure
    return function formatDateValue(date, mask, utc) {
        const dF = DateUtils.formatDate;

        // You can't provide utc if you skip other args (use the "UTC:" mask prefix)
        if (
            arguments.length === 1 &&
            Object.prototype.toString.call(date) === '[object String]' &&
            !/\d/.test(date)
        ) {
            mask = date;
            date = undefined;
        }

        // Passing date through Date applies Date.parse, if necessary
        date = date ? DateUtils.createDateFromString(date) : new Date();
        if (Number.isNaN(date.getTime())) {
            throw new SyntaxError('invalid date');
        }

        mask = String(dF.masks[mask] || mask || dF.masks['default']);

        // Allow setting the utc argument via the mask
        if (mask.slice(0, 4) === 'UTC:') {
            mask = mask.slice(4);
            utc = true;
        }

        const ddd = i18next.t('calendar.weekDaysShort').split(',');
        const mmm = i18next.t('calendar.monthsShort').split(',');
        const mmmm = i18next.t('calendar.months').split(',');

        const _ = utc ? 'getUTC' : 'get';
        const d = date[_ + 'Date']();
        const D = date[_ + 'Day']();
        const m = date[_ + 'Month']();
        const y = date[_ + 'FullYear']();
        const H = date[_ + 'Hours']();
        const M = date[_ + 'Minutes']();
        const s = date[_ + 'Seconds']();
        const L = date[_ + 'Milliseconds']();
        const o = utc ? 0 : date.getTimezoneOffset();
        const flags = {
            d: d,
            dd: pad(d),
            ddd: ddd[D],
            m: m + 1,
            mm: pad(m + 1),
            mmm: mmm[m],
            mmmm: mmmm[m],
            yy: String(y).slice(2),
            yyyy: y,
            h: H % 12 || 12,
            hh: pad(H % 12 || 12),
            H: H,
            HH: pad(H),
            M: M,
            MM: pad(M),
            s: s,
            ss: pad(s),
            l: pad(L, 3),
            L: pad(L > 99 ? Math.round(L / 10) : L),
            t: H < 12 ? 'a' : 'p',
            tt: H < 12 ? 'am' : 'pm',
            T: H < 12 ? 'A' : 'P',
            TT: H < 12 ? 'AM' : 'PM',
            Z: utc ? 'UTC' : (String(date).match(timezone) || ['']).pop().replace(timezoneClip, ''),
            o:
                (o > 0 ? '-' : '+') +
                pad(Math.floor(Math.abs(o) / 60) * 100 + (Math.abs(o) % 60), 4),
            S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : (((d % 100) - (d % 10) !== 10) * d) % 10],
        };

        return mask.replace(token, function replaceToken($0) {
            return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
        });
    };
})();

DateUtils.formatDate.masks = {
    default: 'ddd mmm dd yyyy HH:MM:ss',
    justDay: 'dd',
    justMonth: 'mm',
    shortDate: 'm/d/yy',
    mediumDate: 'mmm d, yyyy',
    longDate: 'mmmm d, yyyy',
    shortTime: 'h:MM TT',
    mediumTime: 'h:MM:ss TT',
    longTime: 'h:MM:ss TT Z',
    isoDate: 'yyyy-mm-dd',
    isoTime: 'HH:MM:ss',
    isoDateTime: 'yyyy-mm-dd\'T\'HH:MM:ss',
    datepicker: 'ddd. d mmmm, yyyy',
    datepickerExtendedMonth: 'ddd. d mmmm, yyyy',
    dayMonth: 'mmm d',
    dayMonthShort: 'dd mmm',
    extendedDayDayMonth: 'ddd, dd mmm',
    hourMinutes: 'HH:MM',
    yearMonth: 'yyyy-mm',
    dayShortWithTime: 'ddd HH:MM',
    dayShortWithTimeCommaSeparated: 'ddd, HH:MM',
    yearMonthDayHoursMinutes: 'yyyy-mm-dd HH:MM',
    dayMonthTime: 'dd mmmm. HH:MM TT',
};
